"use client";

import { useState } from "react";
import { useIntersectionObserver, useTimeout } from "usehooks-ts";

import { VimeoPlayer } from "@up/ui";

const YOUTUBE_EMBED_BASE = "https://www.youtube-nocookie.com/embed/";
const YOUTUBE_PARAMS = "cc_load_policy=1&modestbranding=1&rel=0";

/** Matches `VimeoPlayer`: load anyway if intersection never reports. */
const LOAD_FALLBACK_MS = 8000;

const YOUTUBE_ID_PATTERNS = [
  /[?&]v=([\w-]+)/,
  /youtu\.be\/([\w-]+)/,
  /\/(?:embed|shorts|live|v)\/([\w-]+)/,
];

/**
 * Accepts either a bare YouTube ID or any of the URL shapes an editor is
 * likely to paste (watch, youtu.be, shorts, embed) and returns the ID.
 */
export function youtubeEmbedId(value: string): string {
  const id = value.trim();

  if (!id.includes("/") && !id.includes("?")) {
    return id;
  }

  for (const pattern of YOUTUBE_ID_PATTERNS) {
    const match = id.match(pattern);
    if (match) {
      return match[1];
    }
  }

  return id;
}

export function VideoQuote({
  id,
  orientation,
  source,
  title,
}: {
  id: string;
  orientation: "horizontal" | "vertical" | null;
  source: "vimeo" | "youtube" | null;
  title: string;
}) {
  // Players stay unloaded until the testimonial scrolls into view, so a page
  // with several testimonials does not pay for every embed up front.
  const { isIntersecting, ref } = useIntersectionObserver({
    freezeOnceVisible: true,
  });
  const [didTimeOut, setDidTimeOut] = useState(false);

  useTimeout(() => setDidTimeOut(true), LOAD_FALLBACK_MS);

  const shouldLoad = isIntersecting || didTimeOut;

  return (
    <div
      className={`testimonials2-video testimonials2-video--${
        orientation === "vertical" ? "vertical" : "horizontal"
      }`}
      ref={ref}
    >
      {source === "youtube" ? (
        shouldLoad ? (
          <iframe
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 h-full w-full"
            src={`${YOUTUBE_EMBED_BASE}${youtubeEmbedId(id)}?${YOUTUBE_PARAMS}`}
            style={{ border: 0 }}
            title={title}
          />
        ) : null
      ) : (
        <VimeoPlayer autoplay={false} id={id} shouldLoad={shouldLoad} />
      )}
    </div>
  );
}
